import { useQuery } from "@tanstack/react-query"
import { parse } from "date-fns"
import {
  Cloud,
  CloudDrizzle,
  CloudRain,
  Droplets,
  Eye,
  Gauge,
  Moon,
  Sun,
  Sunrise,
  Sunset,
  Thermometer,
  Wind,
} from "lucide-react"

import { getWeatherForLocation } from "@/api/weatherService.ts"
import { Button } from "@/components/ui/button.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx"
import type { WeatherData } from "@/types/weatherTypes.ts"

interface WeatherDropdownProps {
  locationId: number
}

const getWeatherIcon = (iconCode: string, description: string, weather?: WeatherData) => {
  // Determine if it's currently day or night using sunrise/sunset times
  const isNightTime =
    weather?.sunrise && weather?.sunset
      ? (() => {
          const now = new Date()
          const today = now.toISOString().split("T")[0] // Get YYYY-MM-DD format
          try {
            // Parse times in HH:MM AM/PM format
            const sunrise = parse(`${today} ${weather.sunrise}`, "yyyy-MM-dd hh:mm a", now)
            const sunset = parse(`${today} ${weather.sunset}`, "yyyy-MM-dd hh:mm a", now)
            return now < sunrise || now > sunset
          } catch {
            return false // Fallback if parsing fails
          }
        })()
      : false

  // Use weather icon from backend if available, otherwise fall back to description
  const backendIcon = weather?.icon

  if (backendIcon) {
    switch (backendIcon) {
      case "sunny":
        return isNightTime
          ? {
              icon: <Moon className="h-4 w-4 text-blue-200" />,
              tooltip: `Clear night • ${weather?.moonPhase ?? "Moon"}`,
            }
          : { icon: <Sun className="h-4 w-4 text-yellow-500" />, tooltip: "Clear sky" }
      case "partly-cloudy":
        return { icon: <Cloud className="h-4 w-4 text-gray-400" />, tooltip: "Partly cloudy" }
      case "cloudy":
        return { icon: <Cloud className="h-4 w-4 text-gray-500" />, tooltip: "Cloudy" }
      case "rainy":
        return { icon: <CloudRain className="h-4 w-4 text-blue-500" />, tooltip: "Rainy" }
      case "thunderstorm":
        return { icon: <CloudRain className="h-4 w-4 text-purple-500" />, tooltip: "Thunderstorm" }
      case "snowy":
        return { icon: <Cloud className="h-4 w-4 text-blue-100" />, tooltip: "Snow" }
      case "foggy":
        return { icon: <Cloud className="h-4 w-4 text-gray-400" />, tooltip: "Foggy" }
      case "windy":
        return { icon: <Wind className="h-4 w-4 text-gray-600" />, tooltip: "Windy" }
      default:
        return { icon: <Cloud className="h-4 w-4 text-gray-500" />, tooltip: description }
    }
  }

  // Fallback to legacy icon code logic
  if (iconCode.startsWith("01"))
    return isNightTime
      ? {
          icon: <Moon className="h-4 w-4 text-blue-200" />,
          tooltip: `Clear night • ${weather?.moonPhase || "Moon"}`,
        }
      : { icon: <Sun className="h-4 w-4 text-yellow-500" />, tooltip: "Clear sky" }
  if (iconCode.startsWith("02") || iconCode.startsWith("03"))
    return { icon: <Cloud className="h-4 w-4 text-gray-500" />, tooltip: "Cloudy" }
  if (iconCode.startsWith("09") || iconCode.startsWith("10"))
    return { icon: <CloudRain className="h-4 w-4 text-blue-500" />, tooltip: "Rainy" }
  if (description.toLowerCase().includes("rain"))
    return { icon: <CloudDrizzle className="h-4 w-4 text-blue-600" />, tooltip: "Light rain" }
  return { icon: <Cloud className="h-4 w-4 text-gray-500" />, tooltip: "Cloudy" }
}

const formatLastUpdated = (date: Date, weather?: { lastUpdated?: string }) => {
  // Use weather.lastUpdated if available from backend, otherwise use query timestamp
  const updateTime = weather?.lastUpdated ? new Date(weather.lastUpdated) : date
  const now = new Date()
  const diffMs = now.getTime() - updateTime.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return "Just updated"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return updateTime.toLocaleDateString()
}

export default function WeatherDropdown({ locationId }: WeatherDropdownProps) {
  const {
    data: weather,
    isLoading,
    isError,
    error,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["weather", locationId],
    queryFn: () => getWeatherForLocation(locationId),
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  })

  if (isLoading) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" disabled className="flex items-center gap-1">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-3 w-8" />
              <Skeleton className="weather-summary-description h-3 w-12" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Loading weather...</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  if (isError || !weather) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-muted-foreground"
            >
              <Cloud className="h-4 w-4" />
              <span className="text-xs">N/A</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Weather data unavailable</p>
            {error && <p className="text-xs text-muted-foreground">{error.toString()}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const weatherIconData = getWeatherIcon(weather.icon, weather.description, weather)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
          aria-label={`Weather for ${weather.locationName}`}
        >
          {weatherIconData.icon}
          <span className="font-medium">{weather.temperature}°</span>
          <span className="weather-summary-description text-muted-foreground text-xs">
            {weather.description}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 z-[9999]">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span>Weather</span>
              <span className="text-xs text-muted-foreground">{weather.locationName}</span>
            </div>
            <div className="text-xs font-normal text-muted-foreground">
              Updated: {formatLastUpdated(new Date(dataUpdatedAt), weather)}
              {weather.dataProvider && ` • ${weather.dataProvider}`}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {weatherIconData.icon}
            <span className="text-lg font-bold">{weather.temperature}°F</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Current Conditions */}
        <DropdownMenuItem className="flex-col items-start space-y-2">
          <div className="flex items-center justify-between w-full">
            <span className="font-medium">Current Conditions</span>
            <span className="text-sm text-muted-foreground capitalize">{weather.description}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full text-xs">
            <div className="flex items-center gap-2">
              <Thermometer className="h-3 w-3 text-orange-500" />
              <span>
                Feels like: <strong>{weather.feelsLike}°F</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Wind className="h-3 w-3 text-blue-500" />
              <span>
                Wind: <strong>{weather.windSpeed} mph</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Droplets className="h-3 w-3 text-blue-600" />
              <span>
                Humidity: <strong>{weather.humidity}%</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Gauge className="h-3 w-3 text-gray-500" />
              <span>
                Pressure: <strong>{weather.pressure}"</strong>
              </span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Today's Forecast */}
        <DropdownMenuItem className="flex-col items-start space-y-2">
          <div className="flex items-center justify-between w-full">
            <span className="font-medium">Today's Forecast</span>
            <span className="text-sm text-muted-foreground">
              {weather.high}°/{weather.low}°F
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full text-xs">
            <div className="flex items-center gap-2">
              <CloudDrizzle className="h-3 w-3 text-blue-500" />
              <span>
                Rain chance: <strong>{weather.precipitationChance}%</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CloudRain className="h-3 w-3 text-blue-600" />
              <span>
                {typeof weather.precipitation === "number" ? (
                  weather.precipitation > 0 ? (
                    <>
                      Expected: <strong>{weather.precipitation.toFixed(2)}″</strong>
                    </>
                  ) : (
                    <>
                      Expected: <strong>0″</strong>
                    </>
                  )
                ) : (
                  <>
                    Expected: <strong>N/A</strong>
                  </>
                )}
              </span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Astronomy */}
        {weather.sunrise && weather.sunset && (
          <DropdownMenuItem className="flex-col items-start space-y-2">
            <div className="flex items-center justify-between w-full">
              <span className="font-medium">Astronomy</span>
              {weather.moonPhase && (
                <span className="text-sm text-muted-foreground">
                  Moon Phase: {weather.moonPhase}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 w-full text-xs">
              <div className="flex items-center gap-2">
                <Sunrise className="h-3 w-3 text-orange-500" />
                <span>
                  Sunrise: <strong>{weather.sunrise}</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Sunset className="h-3 w-3 text-orange-600" />
                <span>
                  Sunset: <strong>{weather.sunset}</strong>
                </span>
              </div>
              {weather.moonrise && weather.moonset && (
                <>
                  <div className="flex items-center gap-2">
                    <Moon className="h-3 w-3 text-blue-300" />
                    <span>
                      Moonrise: <strong>{weather.moonrise}</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Moon className="h-3 w-3 text-blue-400" />
                    <span>
                      Moonset: <strong>{weather.moonset}</strong>
                    </span>
                  </div>
                </>
              )}
            </div>
            {weather.moonIllumination && (
              <div className="text-xs text-muted-foreground">
                Moon illumination: <strong>{weather.moonIllumination}</strong>
              </div>
            )}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Data Source */}
        <DropdownMenuItem className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Eye className="h-3 w-3" />
            <span>
              Source: <strong className="capitalize">{weather.source}</strong>
            </span>
          </div>
          <a
            href="https://github.com/chubin/wttr.in"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {weather.dataProvider || (weather.source === "api" ? "wttr.in" : "Internal")}
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
